/**
 * 编排/出图失败时对用户说的话(VID-REFUND-1)。
 *
 * ★这个文件存在的理由是一条**真事故**:失败横幅长期写死「AI 编排失败,请重试(未产生出图扣费)」与
 * 「台本图全部出图失败 ——(失败不扣费)」。字面上它说的是"出图"没扣费,而用户读到的就是"没扣钱" ——
 * 可编排费是**另一笔**,0818 账目实锤:失败任务里 15 条真跑了编排并被扣费,合计 10,829 积分,
 * 而 5 条 PARTIAL 的真实扣费合计 38,004。**那两句话是反着说的。**
 *
 * 为什么不能只把"不扣费"三个字删掉:同一个坑我们栽过四次,**禁一个词它就换个同义词**
 * (「暂时」→「目前」、「不扣钱放心」→「没扣钱」)。所以判据必须**双向**:既不出现"没扣费"这类
 * 断言,又必须出现**真实**的钱信息。本模块把措辞与判据一起收在纯函数里,由单测钉死。
 *
 * 口径纪律(**宁可说读不到,也不许猜**):
 * - 只有在代码层能保证"确实没扣"时才说没扣 —— 即 ai 明确算出空产出(`EMPTY`)/ ai 明确回错
 *   (`UPSTREAM_ERROR`),这两条路 ai 侧票据不会 settle。
 * - 没等到 ai 回包(`TIMEOUT`/`TRANSPORT`)时,ai 很可能已成功扣费,且那笔钱**退不回来**
 *   (ai 的 "refund" 只是不发还没发的那笔,不回滚已 settle 的)。这时**不许**说没扣,也**不许**编一个
 *   金额 —— 前端此刻拿不到真实数字,只能如实说"可能已产生扣费"并把人指向账单。
 */

import { isDeliveredChunk } from "./chunkTally";

/** 后端 `composeFailKind` 取值(与 `VideoAiClient` 常量同源)。 */
export type ComposeFailKind = "EMPTY" | "TIMEOUT" | "TRANSPORT" | "UPSTREAM_ERROR";

/** 重新编排/重新渲染要再花一次钱 —— 说"可以重试"却不说要再付费,是"说一半"。 */
export const RETRY_COSTS_AGAIN = "重新编排会再消耗一次积分";

/** 单镜/单段重渲的再计费提示(账上实测:重渲 -3000、续渲 -2009,都是几千量级)。 */
export const REGENERATE_COSTS_AGAIN = "重新渲染按新任务计费,会再扣一次积分";

/** 账单入口话术:拿不到真实数字时,把人指过去,而不是替他下结论。 */
const CHECK_BILL = "具体以账单明细为准";

/**
 * 编排失败横幅文案。
 *
 * @param kind 后端给的钱口径;**缺失(旧数据/后端没给)时按"不确定"处理** —— 缺席不等于没扣费,
 *   把未知说成"没扣费"正是这次要修的那类假话。
 */
export function composeFailedNotice(kind?: string | null): string {
  switch (kind) {
    case "EMPTY":
      // ai 明确算出空产出 → 票内抛错未 settle → 这一种可以如实说没扣。
      return `AI 编排没能产出可用分镜,本次未扣编排积分。${RETRY_COSTS_AGAIN}。`;
    case "UPSTREAM_ERROR":
      // ai 明确回了错 → 票按失败退掉,同样没 settle。
      return `AI 服务返回失败,本次未扣编排积分。${RETRY_COSTS_AGAIN}。`;
    case "TIMEOUT":
    case "TRANSPORT":
      // 没等到回包:ai 那边很可能已经跑完并扣了费,且退不回来。不许说没扣,也不许编金额。
      return `AI 编排超时,未能取回结果;编排可能已在后台完成并产生扣费,${CHECK_BILL}。${RETRY_COSTS_AGAIN}。`;
    default:
      // 后端没给口径 = 不确定。不确定时同样不许宣称没扣费。
      return `AI 编排失败;本次是否产生扣费${CHECK_BILL}。${RETRY_COSTS_AGAIN}。`;
  }
}

/**
 * 出图部分/全部失败的横幅文案。
 *
 * ★原文案说「失败不扣费」,而 0818 账目显示 PARTIAL 恰恰是扣了钱的(5 条合计 38,004)——
 * 已经出成功的那些镜是真扣了的,失败的镜不另计。所以这里**只陈述已发生的事实**,不做"不扣费"的许诺。
 *
 * ★0901 又栽掉一个猜测:原文案还写「多为出图渠道故障」—— 当天真机全灭的真凶其实是
 * 过期签名(海报锚 URL bootstrap 时签,备料超 1 小时提交必炸),按"渠道故障"指路把
 * 排障方向整个带偏。本模块自己的口径纪律就是「宁可说读不到,也不许猜」—— 猜测删除,
 * 有真实失败原因(reason)就原样透出,没有就只说事实。
 *
 * @param succeeded 已成功出图的镜数
 * @param total 总镜数
 * @param reason 最近一次失败的**真实原因**(后端/ai 报文原文);读不到就别传,不许编
 */
export function frameFailedNotice(
  succeeded: number,
  total: number,
  reason?: string | null
): string {
  const why = reason?.trim() ? `(最近一次失败原因:${reason.trim()})` : "";
  if (succeeded <= 0) {
    return `台本图全部出图失败${why};已成功的镜才计费,本次${
      total > 0 ? `${total} 镜均未产出` : "未产出"
    }。可一键重出,${REGENERATE_COSTS_AGAIN};反复失败请把任务号发给运营排查。`;
  }
  return `部分镜出图失败${why}:${total} 镜中 ${succeeded} 镜已出图并计费。对空缺的镜点「重新生成」补齐,${REGENERATE_COSTS_AGAIN}。`;
}

/* ══════════════════════════════════════════════════════════════════════════
 * PARTIAL(部分段没渲成)的续渲文案 —— 老板 2026-08-19 拍板「详细一点」。
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * 一段的进度。只取判据要的三格;真 DTO 结构上满足(与本仓 RawPosterTask 同姿势)。
 *
 * ★`videoUrl` 是「渲好了」的判据的一半(0926 S2-6):状态写着 SUCCESS 却没有产物地址的段不算渲好 ——
 *   与 `chunkTally.isDeliveredChunk` / backend `hasUndeliveredChunk` 同一口径。
 */
export interface ChunkLike {
  seq: number;
  status: string;
  videoUrl?: string | null;
}

export interface ResumeNotice {
  /** 第一行:发生了什么。 */
  headline: string;
  /** 其余各行。 */
  body: string[];
  /** 已渲好的段数;`null` = 这一格算不出来。仅供判据/埋点核对,不直接渲染。 */
  done: number | null;
  /** 没渲成的那一段的段号;`null` = 算不出来 ⇒ 文案里不出现任何段号。 */
  failedSeq: number | null;
}

/**
 * 续渲文案。
 *
 * ★**段号必须是真值,算不出来就整句退化成不带数字的版本** —— 印一个错数字比不印更糟
 *   (它会把人指到错的那一段去改)。所以这里不"估"也不写死。
 *
 * ★`m`(已渲好几段)可以直接数;`n`(第几段没渲成)只能从「chunkAssets 只含**成功前缀**」
 *   这个不变量推出来。**而那是一句注释,注释会过期**(今晚三次栽在"注释≠今天的代码"上)。
 *   所以这里**在运行时验证不变量**:成功段的 seq 必须连续,连续才敢说下一段就是失败的那段;
 *   一旦不连续(例如 1、2、4 成功而 3 失败),`failedSeq` 返 null、文案自动退化。
 *
 * ★段号用的是 `chunk.seq` 原值 —— 界面上那枚徽章印的就是 `段 {c.seq}`,两处必须同一套编号,
 *   否则文案说的"第 3 段"和用户找到的"段 3"不是同一段。
 *
 * ★「渲好了」= `isDeliveredChunk`(SUCCESS **且**有产物地址),与同一页控制条的 `chunkTally` 同一个判据
 *   (0926 S2-6)。此前这里只看 status:一段 SUCCESS 无 url 时,控制条说「已生成 1/3 段」、这句却说
 *   「前面 2 段已经渲好了」—— 两个数对不上,而且把用户指向一段拿不到的"成功"。
 */
export function partialResumeNotice(chunks: ChunkLike[]): ResumeNotice {
  const doneSeqs = chunks
    .filter(isDeliveredChunk)
    .map((c) => c.seq)
    .sort((a, b) => a - b);
  const done = doneSeqs.length;

  // ── 一段都没渲成 ──────────────────────────────────────────────────────
  // ★独立分支,不是上面那句的降级:这时说"前面 m 段已经渲好了"是假话,
  //   暗示扣了钱同样是假话。依据是代码里那条「零 record 就退票」(独立验证方账上 3 笔零反例)。
  // ★措辞用"没有产生积分消耗"而不是"绝不会扣钱":样本只有 3 笔,撑不起一句承诺。
  // ★不写「可以再试一次」(Z9d 0926):详情页的「整体重试」只在 FAILED/BLOCKED/CANCELLED 渲染,
  //   PARTIAL 态没有那颗按钮 —— 这句会把人指向一个不存在的按钮(0829 已栽过同型:提示叫点
  //   「继续生成」而按钮不在)。而且它本来就不该到:
  //   · api VideoPayloadMapper.isRetryable 只放 FAILED/BLOCKED/CANCELLED,PARTIAL 打 /retry 是 400「当前状态不可重试」;
  //   · 视频 PARTIAL 按设计**必有已交付段**:ai chunk_orchestration 只在 progress.recorded>0 /
  //     已上传块拼装失败时报 PARTIAL(resume 的 PARTIAL 零 record 时前缀来自上一笔票,仍有段);
  //     api MediaCompletionService 的两道盾(FAILED+hasDeliveredChunkPrefix→PARTIAL、
  //     SUCCESS+hasUndeliveredChunk→PARTIAL)与 recomputeStatus 的 (ok==0 ? FAILED : PARTIAL)
  //     都要求至少一段 SUCCESS。所以「PARTIAL 且零交付」是数据异常态,不是要给按钮的正常路;
  //     真到了这里,能做的只有说清出路 = 回视频舱重新创建。
  if (done === 0) {
    return {
      headline: "这次一段都没渲成。",
      body: [
        "没有产生积分消耗。这一单在这里不能重来(部分完成的任务后端不接受重跑),请回视频舱重新创建。",
      ],
      done: 0,
      failedSeq: null,
    };
  }

  const contiguous = doneSeqs.every((s, i) => i === 0 || s === doneSeqs[i - 1] + 1);
  const failedSeq = contiguous ? doneSeqs[done - 1] + 1 : null;

  return {
    headline: failedSeq === null ? "还有段没渲成。" : `第 ${failedSeq} 段没渲成。`,
    body: [
      `前面 ${done} 段已经渲好了,在「分镜渲染结果」里可以逐段查看,也可以直接「合成成片」。`,
      // ★只陈述计费口径,不做"不会白扣"式的承诺 —— 样本撑不起承诺。
      "关于积分:这次只对真正渲出来的段计费。",
      "⚠️ 所以可能出现「扣了钱、但整条片子还不完整」—— 那不是重复扣费,是这一次只补上了一部分。",
      // ★不写方位(别写"点上方/下方的继续生成"):布局会变,按钮名字不会。已栽过一次。
      "要接着补后面的段,点「继续生成」。具体金额以积分流水为准。",
    ],
    done,
    failedSeq,
  };
}
